import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import apiClient from "../../api-services/ApiClient";
import AdminLayout from "../../app-components/AdminLayout";
import Alert from "../../app-components/alert/Alert";
import ActiveFilters, {
  FilterChip,
} from "../../app-components/ui/ActiveFilters";
import Avatar from "../../app-components/ui/Avatar";
import ColumnMenu, {
  ColumnDef,
  useColumnVisibility,
} from "../../app-components/ui/ColumnMenu";
import DateFilter from "../../app-components/ui/DateFilter";
import EmptyState from "../../app-components/ui/EmptyState";
import PageHeader from "../../app-components/ui/PageHeader";
import Pagination from "../../app-components/ui/Pagination";
import StatusPill from "../../app-components/ui/StatusPill";
import {
  btnPrimary,
  btnSecondary,
  card,
  inputBase,
} from "../../app-components/ui/styles";
import TableSkeleton from "../../app-components/ui/TableSkeleton";
import { SITELINKS } from "../../app-routes/links";
import { Icon_Dots } from "../../assets/Icons";
import { ParamInterface, UserInterface } from "../../interface/users.interface";
import {
  csvCell,
  formatDate,
  FUNNEL_LEGEND,
  getFunnelStage,
} from "../../utils";

const AGE_OPTIONS = [
  { value: "18,24", label: "18 - 24" },
  { value: "25,40", label: "25 - 40" },
  { value: "41,50", label: "41 - 50" },
  { value: "51,60", label: "51 - 60" },
  { value: "60,100", label: "60 and above" },
];

const TABS = [
  { value: "", label: "All Users" },
  { value: "ACTIVE", label: "Active" },
  { value: "FLAGGED", label: "Flagged" },
  { value: "REMOVED", label: "Removed" },
];

// Optional columns the admin can show or hide. Stage, Name and Action are always shown.
const COLUMNS: ColumnDef[] = [
  { key: "sn", label: "S/N" },
  { key: "profilePhoto", label: "Profile Photo" },
  { key: "email", label: "LinkedIn Email" },
  { key: "username", label: "Username", defaultVisible: false },
  { key: "uniqueNo", label: "Unique No", defaultVisible: false },
  { key: "phone", label: "Phone" },
  { key: "phoneVerified", label: "Phone Verified", defaultVisible: false },
  { key: "country", label: "Country" },
  { key: "state", label: "State" },
  { key: "date", label: "Request Date" },
  { key: "gender", label: "Gender", defaultVisible: false },
  { key: "status", label: "Status" },
];

const th =
  "py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-hcMuted bg-hcHead sticky top-0 z-10 whitespace-nowrap";
const td = "py-3 px-4 text-left text-sm whitespace-nowrap";

const EliteUser = () => {
  const [usersData, setUsersData] = useState<UserInterface[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(0);
  const [total, setTotal] = useState<number | undefined>(undefined);
  const [pageFilter, setPageFilter] = useState<string>("");

  const [searchInput, setSearchInput] = useState<string>("");
  const [searchInputDebounce, setSearchInputDebounce] = useState<string>("");
  const [ageRange, setAgeRange] = useState<string>("");
  const [dateRange, setDateRange] = useState<string>("");
  const [subscriptionPlan] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [hasError, setHasError] = useState<boolean>(false);

  const { isVisible, toggle, visibleCount } = useColumnVisibility(
    "cols_elite_users",
    COLUMNS
  );
  const show = (key: string) => isVisible(key);

  // effect for get users list
  useEffect(() => {
    const params = {
      page: currentPage,
      status: pageFilter,
      search: searchInput,
      ageRange: ageRange,
      dateRange: dateRange,
      subscriptionPlan: subscriptionPlan,
    };

    getUsers(params);
  }, [
    currentPage,
    pageFilter,
    searchInput,
    ageRange,
    dateRange,
    subscriptionPlan,
  ]);

  // debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchInput(searchInputDebounce);
      setCurrentPage(1);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [searchInputDebounce]);

  useEffect(() => {
    setAlertMessage("");

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (end >= start) {
        setDateRange(startDate + "," + endDate);
        setCurrentPage(1);
      } else {
        setAlertMessage("Invalid date range.");
      }
    }
    if (!startDate && !endDate) {
      setDateRange("");
    }
  }, [startDate, endDate]);

  const getUsers = async (params: ParamInterface) => {
    setLoading(true);
    setHasError(false);
    try {
      const res: any = await apiClient.get("elite-users", { params });
      if (res && res.data) {
        setUsersData(res.data.data);
        setCurrentPage(res.data.current_page);
        setLastPage(res.data.last_page);
        setTotal(res.data.total);
      } else {
        setUsersData([]);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setAlertMessage("Unable to fetch users at the moment.");
      setHasError(true);
      setUsersData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id: number, status: string) => {
    try {
      // Create a copy of the current users data for rollback in case of failure
      const previousUsersData = [...usersData];

      // Optimistically update the UI
      const updatedUsersData = usersData.map((user: any) =>
        user.id === id ? { ...user, status } : user
      );
      setUsersData(updatedUsersData);

      // Make the API call
      const res = await apiClient.patch(`elite-users/${id}/status`, { status });

      // Check if the response is successful
      if (res && res.status === 200 && res.data?.user) {
        // Replace the updated user with the fresh data from the server response
        const refreshedUsersData = usersData.map((user: any) =>
          user.id === id ? res.data.user : user
        );

        setUsersData(refreshedUsersData);
      } else {
        setUsersData(previousUsersData);
      }
    } catch (error) {
      console.error("Error while updating status:", error);
    }
  };

  const handleExport = () => {
    const headers = [
      "LinkedIn Full Name",
      "LinkedIn Email",
      "Username",
      "Unique No",
      "Phone",
      "Phone Verified",
      "Country",
      "State",
      "Stage",
      "Elite Status",
      "Request Date",
      "Gender",
    ];
    const rows = usersData.map((u) => [
      u.linkedin_full_name,
      u.linkedin_email,
      u.username,
      u.id.toString().padStart(6, "0"),
      u.phone_number,
      u.is_phone_verified ? "Yes" : "No",
      u.country?.name,
      u.state?.name,
      getFunnelStage(u).label,
      u.elite_status ?? "Pending",
      u.created_at ? formatDate(u.created_at, "date") : "",
      u.gender,
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map(csvCell).join(","))
      .join("\n");
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8;" })
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = `elite-users-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const setDates = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
  };

  const clearFilters = () => {
    setSearchInputDebounce("");
    setSearchInput("");
    setAgeRange("");
    setDates("", "");
    setCurrentPage(1);
  };

  // Chips for every filter currently applied
  const chips: FilterChip[] = [];
  if (searchInput)
    chips.push({
      label: `Search: ${searchInput}`,
      onRemove: () => {
        setSearchInputDebounce("");
        setSearchInput("");
        setCurrentPage(1);
      },
    });
  if (ageRange)
    chips.push({
      label: `Age: ${
        AGE_OPTIONS.find((a) => a.value === ageRange)?.label ?? ageRange
      }`,
      onRemove: () => {
        setAgeRange("");
        setCurrentPage(1);
      },
    });
  if (dateRange)
    chips.push({
      label: `Date: ${startDate} to ${endDate}`,
      onRemove: () => setDates("", ""),
    });

  const hasRecords = usersData?.length > 0;

  // Compute the starting serial number for the current page (page 1 -> 1, page 2 -> 11, ...)
  const serialStart = (currentPage - 1) * 10 + 1;

  return (
    <AdminLayout title="Admin | Elite Users">
      <PageHeader
        title="Elite Users"
        subtitle="Review and approve elite member requests"
        actions={
          <button
            onClick={handleExport}
            disabled={!hasRecords}
            className={btnSecondary}
          >
            Export CSV
          </button>
        }
      />

      <div className={`${card} py-5`}>
        {/* tabs */}
        <div className="px-5 flex items-center gap-1 mb-5 border-b border-hcBorder">
          {TABS.map((t) => (
            <button
              key={t.value}
              onClick={() => {
                setPageFilter(t.value);
                setCurrentPage(1);
              }}
              className={`px-4 pb-3 text-sm font-medium -mb-px border-b-2 transition ${
                pageFilter === t.value
                  ? "text-honeConnectPurple border-honeConnectPurple"
                  : "text-honeConnectGray border-transparent hover:text-honeConnectBlack"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* filter bar */}
        <div className="px-5 flex items-center gap-3 flex-wrap mb-4">
          <div className="relative flex-1 min-w-[240px]">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg
                className="h-4 w-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                ></path>
              </svg>
            </span>
            <input
              value={searchInputDebounce}
              onChange={(e) => setSearchInputDebounce(e.target.value)}
              type="text"
              className={`${inputBase} pl-9 w-full`}
              placeholder="Search by name, username, unique number, location or interest"
            />
          </div>

          <select
            aria-label="Age"
            className={inputBase}
            value={ageRange}
            onChange={(e) => {
              setAgeRange(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All ages</option>
            {AGE_OPTIONS.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </select>

          <ColumnMenu columns={COLUMNS} isVisible={isVisible} onToggle={toggle} />
        </div>

        <div className="px-5 mb-4">
          <DateFilter
            startDate={startDate}
            endDate={endDate}
            onChange={setDates}
          />
        </div>

        <ActiveFilters chips={chips} onClearAll={clearFilters} />

        {/* funnel legend */}
        <div className="px-5 flex items-center gap-4 mb-4 flex-wrap text-xs text-honeConnectGray">
          {FUNNEL_LEGEND.map((l) => (
            <span key={l.label} className="flex items-center gap-1.5">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: l.color }}
              />
              {l.label}
            </span>
          ))}
        </div>

        {/* record table */}
        {hasError && !loading ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center border-y border-hcBorder">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4">
              <svg
                className="w-7 h-7 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p className="font-semibold text-honeConnectBlack mb-1">
              Something went wrong
            </p>
            <p className="text-sm text-hcMuted mb-4">
              We couldn't load elite users. Please try again.
            </p>
            <button
              onClick={() =>
                getUsers({
                  page: currentPage,
                  status: pageFilter,
                  search: searchInput,
                  ageRange: ageRange,
                  dateRange: dateRange,
                  subscriptionPlan: subscriptionPlan,
                } as ParamInterface)
              }
              className={btnPrimary}
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="overflow-auto max-h-[68vh] border-y border-hcBorder">
            <table className="min-w-full bg-white">
              <thead>
                <tr>
                  <th className={th}>Stage</th>
                  {show("sn") && <th className={th}>S/N</th>}
                  <th
                    className={`${th} left-0 z-20`}
                    style={{ position: "sticky" }}
                  >
                    LinkedIn Name
                  </th>
                  {show("profilePhoto") && (
                    <th className={th}>Profile Photo</th>
                  )}
                  {show("email") && <th className={th}>LinkedIn Email</th>}
                  {show("username") && <th className={th}>Username</th>}
                  {show("uniqueNo") && <th className={th}>Unique No</th>}
                  {show("phone") && <th className={th}>Phone</th>}
                  {show("phoneVerified") && (
                    <th className={th}>Phone Verified</th>
                  )}
                  {show("country") && <th className={th}>Country</th>}
                  {show("state") && <th className={th}>State</th>}
                  {show("date") && <th className={th}>Request Date</th>}
                  {show("gender") && <th className={th}>Gender</th>}
                  {show("status") && <th className={th}>Status</th>}
                  <th className={`${th} text-center`}>Action</th>
                </tr>
              </thead>

              {loading ? (
                <TableSkeleton columns={visibleCount + 3} />
              ) : (
                <tbody className="text-honeConnectBlack">
                  {usersData?.map((user: UserInterface, index: number) => (
                    <tr
                      key={user.id}
                      className="group border-b border-hcBorder hover:bg-hcBg"
                    >
                      <td className={td}>
                        <span
                          title={getFunnelStage(user).label}
                          className="inline-block w-2.5 h-2.5 rounded-full"
                          style={{
                            backgroundColor: getFunnelStage(user).color,
                          }}
                        />
                      </td>

                      {show("sn") && (
                        <td className={`${td} text-hcMuted`}>
                          {serialStart + index}
                        </td>
                      )}

                      <td
                        className={`${td} font-medium bg-white group-hover:bg-hcBg z-[1]`}
                        style={{ position: "sticky", left: 0 }}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar
                            name={user.linkedin_full_name}
                            src={user.linkedin_photo}
                          />
                          <span>{user.linkedin_full_name ?? "no name"}</span>
                        </div>
                      </td>

                      {show("profilePhoto") && (
                        <td className={td}>
                          <Avatar
                            name={user.full_name}
                            src={user.profile_pix_link}
                          />
                        </td>
                      )}

                      {show("email") && (
                        <td className={td}>
                          {user.linkedin_email ?? "no email"}
                        </td>
                      )}

                      {show("username") && (
                        <td className={td}>{user.username ?? "no username"}</td>
                      )}

                      {show("uniqueNo") && (
                        <td className={td}>
                          {user.id.toString().padStart(6, "0")}
                        </td>
                      )}

                      {show("phone") && (
                        <td className={td}>
                          {user.phone_number ?? "no phone"}
                        </td>
                      )}

                      {show("phoneVerified") && (
                        <td className={td}>
                          <StatusPill
                            label={user?.is_phone_verified ? "Yes" : "No"}
                          />
                        </td>
                      )}

                      {show("country") && (
                        <td className={td}>{user.country?.name ?? "-"}</td>
                      )}

                      {show("state") && (
                        <td className={td}>{user.state?.name ?? "-"}</td>
                      )}

                      {show("date") && (
                        <td className={td}>
                          {user.created_at
                            ? formatDate(user.created_at, "date")
                            : ""}
                        </td>
                      )}

                      {show("gender") && (
                        <td className={`${td} capitalize`}>{user?.gender}</td>
                      )}

                      {show("status") && (
                        <td className={td}>
                          {user.elite_status ? (
                            <StatusPill label={user.elite_status} />
                          ) : (
                            <StatusPill label="N/A" tone="neutral" />
                          )}
                        </td>
                      )}

                      <td className={`${td} text-center`}>
                        <div className="dropdown relative">
                          <button
                            title="More Options"
                            className="text-blue-600 dropbtn p-1 rounded hover:bg-hcBg"
                          >
                            <img
                              src={Icon_Dots}
                              alt=""
                              className="text-center"
                            />
                          </button>
                          <div
                            className="dropdown-content absolute shadow-pop bg-white rounded-xl z-30 w-[150px] border border-hcBorder overflow-hidden"
                            style={{ right: "0" }}
                          >
                            <button
                              className="block p-2 text-center w-full hover:bg-honeConnectPurple hover:text-white"
                              onClick={() =>
                                handleStatusUpdate(user.id, "REMOVED")
                              }
                            >
                              Remove Elitist
                            </button>
                            <button
                              className="block p-2 text-center w-full hover:bg-honeConnectPurple hover:text-white"
                              onClick={() =>
                                handleStatusUpdate(user.id, "APPROVED")
                              }
                            >
                              Approve Elitist
                            </button>
                            <Link
                              to={SITELINKS.USER_PROFILE + user.id}
                              className="block p-2 text-center w-full hover:bg-honeConnectPurple hover:text-white"
                            >
                              View Profile
                            </Link>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>

            {!loading && !hasRecords && (
              <EmptyState
                title="No elite users found"
                message={
                  chips.length || pageFilter
                    ? "Try changing your search or filters."
                    : "There are no elite users to display yet."
                }
                onClear={chips.length ? clearFilters : undefined}
              />
            )}
          </div>
        )}

        {hasRecords && lastPage > 1 && (
          <Pagination
            currentPage={currentPage}
            lastPage={lastPage}
            total={total}
            onPageChange={setCurrentPage}
          />
        )}
      </div>

      <Alert message={alertMessage} />
    </AdminLayout>
  );
};

export default EliteUser;
